import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaDotCircle }     from '@react-icons/all-files/fa/FaDotCircle';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog }           from '@react-icons/all-files/fa/FaCog';
import { FaHome }          from '@react-icons/all-files/fa/FaHome';
import { FaSave }          from '@react-icons/all-files/fa/FaSave';
import { FaArrowLeft }     from '@react-icons/all-files/fa/FaArrowLeft';
import Breadcrumb   from '../../../../commons/Breadcrumb';
import PageHeader   from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import UsuariosFilter from '../../../../commons/UsuariosFilter';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, updateEntity, reset } from '../../../../manager/workflow-nodo-instancia.reducer';

const BACK = '/configuracion/workflow/instancias-nodos';

const ESTADO_OPTIONS = [
  { value: 'PENDIENTE',  label: 'Pendiente' },
  { value: 'GENERADO',   label: 'Generado' },
  { value: 'EJECUCION',  label: 'En Ejecución' },
  { value: 'FINALIZADO', label: 'Finalizado' },
  { value: 'CANCELADO',  label: 'Cancelado' },
];

export default function InstanciasNodosFormPage() {
  const { id }    = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const dispatch  = useAppDispatch();

  const entity        = useAppSelector(s => (s as any).workflowNodoInstancia.entity);
  const updating      = useAppSelector(s => (s as any).workflowNodoInstancia.updating);
  const updateSuccess = useAppSelector(s => (s as any).workflowNodoInstancia.updateSuccess);
  const errorMessage  = useAppSelector(s => (s as any).workflowNodoInstancia.errorMessage);

  const [estado,           setEstado]           = useState('');
  const [usuarioAsignado,  setUsuarioAsignado]  = useState<any>(null);
  const [usuarioAutoriza,  setUsuarioAutoriza]  = useState<any>(null);

  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    if (id) dispatch(getEntity(id));
    return () => { dispatch(reset()); };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (entity?.id) {
      setEstado(entity.estado ?? '');
      setUsuarioAsignado(entity.usuarioAsignado ?? null);
      setUsuarioAutoriza(entity.usuarioAutoriza  ?? null);
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, { state: { successMsg: 'Instancia de nodo actualizada correctamente.' } });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (errorMessage) showToast('error', errorMessage);
  }, [errorMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(updateEntity({
      ...entity,
      estado,
      usuarioAsignado,
      usuarioAutoriza,
    }));
  };

  // ── Read-only info helpers ────────────────────────────────────────────────
  const empresa  = entity?.workflowInstancia?.workflowEmpresa?.empresa;
  const workflow = entity?.workflowInstancia?.workflowEmpresa?.workflow;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio',        to: '/',                      icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion',         icon: <FaCog /> },
        { label: 'Workflow',      to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Instancias de Nodos', to: BACK,              icon: <FaDotCircle /> },
        { label: `Editar #${id}` },
      ]} />

      <PageHeader
        icon={<FaDotCircle />}
        iconColor="#ef4444"
        title={`Editar Instancia de Nodo #${id}`}
        subtitle="Modifica el estado y usuarios asignados"
      />

      {alert && <PageAlert alert={alert} />}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* ── Read-only info card ─────────────────────────────────────── */}
        <div style={s.card}>
          <p style={s.cardTitle}>Información de la instancia</p>
          <div style={s.infoGrid}>
            <InfoRow label="ID"          value={entity?.id} />
            <InfoRow label="RFC"         value={empresa?.rfc} />
            <InfoRow label="Razón Social" value={empresa?.razonSocial} />
            <InfoRow label="Grupo"       value={empresa?.grupoEmpresarial?.alias} />
            <InfoRow label="Workflow"    value={workflow?.nombre} />
            <InfoRow label="Estado WF"   value={entity?.workflowInstancia?.estado} />
            <InfoRow label="Resultado"   value={entity?.resultado} />
            <InfoRow label="Fecha Inicio"
              value={entity?.fechaInicio ? new Date(entity.fechaInicio).toLocaleDateString('es-MX') : undefined} />
            <InfoRow label="Fecha Autor."
              value={entity?.fechaAutorizacion ? new Date(entity.fechaAutorizacion).toLocaleDateString('es-MX') : undefined} />
          </div>
        </div>

        {/* ── Editable fields ─────────────────────────────────────────── */}
        <div style={s.card}>
          <p style={s.cardTitle}>Campos editables</p>
          <div style={s.editGrid}>

            {/* Estado */}
            <div style={s.fieldGroup}>
              <label style={s.label}>Estado</label>
              <select
                value={estado}
                onChange={e => setEstado(e.target.value)}
                style={s.select}
              >
                <option value="">— Seleccionar —</option>
                {ESTADO_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Usuario Asignado */}
            <div style={s.fieldGroup}>
              <label style={s.label}>Usuario Asignado</label>
              <UsuariosFilter
                value={usuarioAsignado}
                onChange={setUsuarioAsignado}
                placeholder="Buscar usuario asignado…"
              />
            </div>

            {/* Usuario Autoriza */}
            <div style={s.fieldGroup}>
              <label style={s.label}>Usuario Autoriza</label>
              <UsuariosFilter
                value={usuarioAutoriza}
                onChange={setUsuarioAutoriza}
                placeholder="Buscar usuario que autoriza…"
              />
            </div>

          </div>
        </div>

        {/* ── Actions ─────────────────────────────────────────────────── */}
        <div style={s.actions}>
          <button type="button" style={s.btnCancel} onClick={() => navigate(BACK)}>
            <FaArrowLeft style={{ marginRight: 6 }} /> Cancelar
          </button>
          <button type="submit" style={s.btnSave} disabled={updating}>
            <FaSave style={{ marginRight: 6 }} />
            {updating ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>

      </form>
    </div>
  );
}

// ── Read-only info row ────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value?: any }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={s.label}>{label}</span>
      <span style={s.readOnly}>{value ?? '—'}</span>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  card: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  cardTitle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: '#475569',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '12px 20px',
  },
  editGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '16px 20px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  readOnly: {
    fontSize: 13,
    color: '#1e293b',
    padding: '4px 0',
    borderBottom: '1px solid #f1f5f9',
  },
  select: {
    padding: '8px 10px',
    border: '1px solid var(--crm-border)',
    borderRadius: 7,
    fontSize: 13,
    color: 'var(--crm-text-primary)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
    background: 'var(--crm-input-bg)',
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  btnCancel: {
    display: 'inline-flex', alignItems: 'center',
    padding: '9px 18px', background: '#fff', color: '#475569',
    border: '1px solid #e2e8f0', borderRadius: 8,
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  btnSave: {
    display: 'inline-flex', alignItems: 'center',
    padding: '9px 18px', background: '#ef4444', color: '#fff',
    border: 'none', borderRadius: 8,
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
};
